import { ReactNode, useEffect, useState } from 'react';
import { Button, Tabs, Typography } from 'antd';
import { StarFilled, StarOutlined } from '@ant-design/icons';

export interface AreaSubTabsArea {
	id: number;
	name: string;
}

interface Props {
	areas: AreaSubTabsArea[];
	value: number | null;
	onChange: (id: number) => void;
	storageKey: string;
	rightExtra?: ReactNode;
}

/** Sub-tab strip with a star-pin per tab that persists a per-tablet default
 *  via localStorage. When pinned, collapses to a single-area header so a
 *  tablet dedicated to e.g. "JSA Cell" hides the other cells. */
export function AreaSubTabs({ areas, value, onChange, storageKey, rightExtra }: Props) {
	const [pinnedId, setPinnedId] = useState<number | null>(
		() => Number(localStorage.getItem(storageKey) || '') || null,
	);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {
		if (value != null) return;
		if (areas.length === 0) return;
		const defaultArea = areas.find((a) => a.id === pinnedId) ?? areas[0];
		onChange(defaultArea.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [areas, storageKey]);

	if (areas.length === 0) return null;

	const pinnedArea = pinnedId != null ? (areas.find((a) => a.id === pinnedId) ?? null) : null;
	const collapsed = pinnedArea != null && !expanded;

	const pin = (id: number) => {
		localStorage.setItem(storageKey, String(id));
		setPinnedId(id);
		onChange(id);
		setExpanded(false);
	};
	const unpin = () => {
		localStorage.removeItem(storageKey);
		setPinnedId(null);
		setExpanded(true);
	};

	// Pinned-collapsed mode: only the pinned area's name + an unpin star.
	// A "Show all" link lets the operator temporarily reveal other cells.
	if (collapsed) {
		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 10,
					padding: '8px 4px',
					borderBottom: '1px solid #f0f0f0',
				}}
			>
				<Typography.Text strong style={{ fontSize: 14 }}>
					{pinnedArea!.name}
				</Typography.Text>
				<span
					role="button"
					aria-label={`Unpin ${pinnedArea!.name}`}
					onClick={unpin}
					style={{ cursor: 'pointer', lineHeight: 1 }}
					title="Unpin (show all cells)"
				>
					<StarFilled style={{ color: '#faad14' }} />
				</span>
				{areas.length > 1 && (
					<Button type="link" size="small" onClick={() => setExpanded(true)} style={{ padding: 0 }}>
						Show all
					</Button>
				)}
				<span style={{ flex: 1 }} />
				{rightExtra}
			</div>
		);
	}

	// Standard tabs mode — show each area with a star to pin.
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
			<div style={{ flex: 1, minWidth: 0 }}>
				<Tabs
					activeKey={value != null ? String(value) : undefined}
					onChange={(k) => onChange(Number(k))}
					items={areas.map((a) => ({
						key: String(a.id),
						label: (
							<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
								{a.name}
								<span
									role="button"
									aria-label={`Pin ${a.name} as default`}
									onClick={(e) => {
										e.stopPropagation();
										pin(a.id);
									}}
									style={{ cursor: 'pointer', lineHeight: 1 }}
								>
									{pinnedId === a.id ? (
										<StarFilled style={{ color: '#faad14' }} />
									) : (
										<StarOutlined style={{ color: '#bfbfbf' }} />
									)}
								</span>
							</span>
						),
					}))}
				/>
			</div>
			{rightExtra}
		</div>
	);
}
